import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  Rows3,
  Radio,
  Calendar,
  Wallet,
  Trash2,
  Settings as SettingsIcon,
  Shield,
  Search,
  Train,
  ChevronDown,
} from "lucide-react";

/**
 * Sidebar — 244px sticky-full-height navigation rail (Mission Control chrome).
 *
 * Layout (top → bottom):
 *   1. Brand pod      (gradient logo + RailSync. wordmark + PRO · v2.4 overline)
 *   2. Search button  (placeholder ⌘K command palette trigger)
 *   3. Workspace nav  (Mission Control, Journeys, Live, Calendar, Wallet, Trash)
 *   4. Account nav    (Settings, Security)
 *   5. spacer
 *   6. System status  (all-systems pulse + 4-service mini bars)
 *   7. User pod       (avatar + name + email + chevron) — can be replaced
 *      with a wrapper via the `userMenuSlot` render prop when the parent
 *      needs a DropdownMenu (so the trigger lives inside the user pod
 *      and the menu items can dispatch to /settings, /logout, etc.).
 *
 * Workspace items are NOT routes — they're VIEW FILTERS within /dashboard.
 * The parent owns `activeView` state and a `onViewChange(id)` callback.
 *
 * Props:
 *   user           — { name, email, ... } from AuthContext-equivalent
 *   counts         — { journeys, live, trash } badge counts (defaults to 0)
 *   activeView     — current workspace view id; defaults to "mission-control"
 *   onViewChange   — (id: string) => void — fires when a workspace item is clicked
 *   onCommandOpen  — opens the ⌘K palette (stub for now)
 *   onUserMenu     — opens the user dropdown
 *   userMenuSlot   — optional render prop. Receives the inner user-pod JSX so
 *                    the parent can wrap it in a DropdownMenu trigger. If
 *                    omitted, falls back to a plain <button onClick={onUserMenu}>.
 *   health         — { allOk, latencyMs, services: {irctc,ntes,calendar,wallet} }
 */

const NavItem = ({ to, icon, label, active, badge, hint, onClick }) => {
  const className = `group w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13.5px] transition-colors text-left ${
    active
      ? "bg-indigo-500/15 text-zinc-50 border border-indigo-500/30"
      : "text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/40 border border-transparent"
  }`;
  const iconCls = active
    ? "text-indigo-400"
    : "text-zinc-500 group-hover:text-zinc-300";

  const inner = (
    <>
      <span className={iconCls}>{icon}</span>
      <span className="flex-1 truncate">{label}</span>
      {badge != null && (
        <span
          className={`px-1.5 py-0.5 rounded text-[10px] font-medium font-mono ${
            active
              ? "bg-indigo-500/20 text-indigo-300"
              : "bg-zinc-800 text-zinc-400"
          }`}
        >
          {badge}
        </span>
      )}
      {hint && (
        <span className="text-[10px] text-zinc-600 font-mono">{hint}</span>
      )}
    </>
  );

  if (to) {
    return (
      <Link to={to} className={className} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      {inner}
    </button>
  );
};

const Sidebar = ({
  user,
  counts = { journeys: 0, live: 0, trash: 0 },
  active,
  activeView = "mission-control",
  onViewChange,
  onCommandOpen,
  onUserMenu,
  userMenuSlot,
  health,
}) => {
  const location = useLocation();
  // On the /settings route nothing in the workspace nav should highlight.
  // Otherwise the highlighted item tracks the activeView prop.
  const onSettings = location.pathname.startsWith("/settings");
  const derivedActive = active ?? (onSettings ? "settings" : activeView);

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  const allSystemsOk = health?.allOk ?? true;
  const latency = health?.latencyMs ?? null;

  // Reflect actual service tone in the 4-bar mini-grid; default optimistic.
  const services = health?.services || {
    irctc: "operational",
    ntes: "operational",
    calendar: "operational",
    wallet: "operational",
  };
  const toneFor = (s) =>
    s === "down"
      ? "bg-rose-400/80"
      : s === "degraded"
        ? "bg-amber-400/80"
        : "bg-emerald-400/70";

  // Workspace items dispatch view changes via onViewChange. If the parent
  // didn't wire onViewChange, fall back to a /dashboard Link (legacy
  // behaviour) — keeps the sidebar functional outside the Pro Dashboard
  // composition.
  const makeViewItem = (id, opts) => {
    if (onViewChange) {
      return { id, ...opts, onClick: () => onViewChange(id) };
    }
    return { id, ...opts, to: "/dashboard" };
  };

  const workspace = [
    makeViewItem("mission-control", { icon: <LayoutDashboard size={16} />, label: "Mission Control", hint: "G D" }),
    makeViewItem("journeys",        { icon: <Rows3 size={16} />,           label: "Journeys",         badge: counts.journeys || undefined }),
    makeViewItem("live",            { icon: <Radio size={16} />,           label: "Live Tracking",    badge: counts.live || undefined }),
    makeViewItem("calendar",        { icon: <Calendar size={16} />,        label: "Calendar",         hint: "G C" }),
    makeViewItem("wallet",          { icon: <Wallet size={16} />,          label: "Wallet Passes" }),
    makeViewItem("trash",           { icon: <Trash2 size={16} />,          label: "Trash",            badge: counts.trash || undefined }),
  ];
  const account = [
    { id: "settings", to: "/settings",   icon: <SettingsIcon size={16} />,    label: "Settings" },
    { id: "shield",   to: "/settings",   icon: <Shield size={16} />,          label: "Security",         badge: user?.totp_enabled || user?.is_2fa_enabled ? "2FA" : undefined },
  ];

  return (
    <aside
      className="w-[244px] shrink-0 border-r border-zinc-800/60 bg-zinc-950/80 flex flex-col h-screen sticky top-0 z-30"
      data-testid="sidebar"
    >
      {/* Brand */}
      <div className="px-4 pt-5 pb-4 flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-400 to-indigo-600 flex items-center justify-center shadow-[0_0_22px_rgba(99,102,241,.35)]">
          <Train size={16} strokeWidth={2} className="text-white" />
        </div>
        <div className="flex-1">
          <div className="text-[15px] font-bold text-zinc-50 tracking-tight font-heading">
            RailSync<span className="text-indigo-400">.</span>
          </div>
          <div className="text-[10px] text-zinc-500 tracking-[0.18em] uppercase font-mono">
            PRO · v2.4
          </div>
        </div>
      </div>

      {/* Search / Command */}
      <div className="px-3 pb-3">
        <button
          type="button"
          onClick={onCommandOpen}
          className="w-full flex items-center gap-2 px-3 py-2 bg-zinc-900/70 border border-zinc-800 hover:border-zinc-700 rounded-lg text-zinc-500 text-[12.5px] transition-colors"
          data-testid="sidebar-search-btn"
        >
          <Search size={14} />
          <span className="flex-1 text-left">Search PNR, train…</span>
          <kbd className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[10px] border border-zinc-700/80 font-mono">
            ⌘K
          </kbd>
        </button>
      </div>

      {/* Workspace nav */}
      <div className="px-3 mb-2">
        <div className="px-2 mb-1.5 text-[10px] text-zinc-600 tracking-[0.22em] font-semibold uppercase font-mono">
          Workspace
        </div>
        <div className="flex flex-col gap-1">
          {workspace.map((it) => (
            <NavItem key={it.id} {...it} active={it.id === derivedActive} />
          ))}
        </div>
      </div>

      {/* Account nav */}
      <div className="px-3 mb-2">
        <div className="px-2 mb-1.5 mt-3 text-[10px] text-zinc-600 tracking-[0.22em] font-semibold uppercase font-mono">
          Account
        </div>
        <div className="flex flex-col gap-1">
          {account.map((it) => (
            <NavItem key={it.id} {...it} active={it.id === derivedActive} />
          ))}
        </div>
      </div>

      <div className="flex-1" />

      {/* System status footer */}
      <div
        className="mx-3 mb-3 p-3 rounded-lg bg-zinc-900/60 border border-zinc-800/70"
        data-testid="sidebar-status"
      >
        <div className="flex items-center gap-2 mb-3 whitespace-nowrap">
          <span className="relative inline-flex w-2 h-2 shrink-0">
            {allSystemsOk && (
              <span className="absolute inset-0 rounded-full bg-emerald-400/50 animate-ping" />
            )}
            <span
              className={`relative w-2 h-2 rounded-full ${
                allSystemsOk ? "bg-emerald-400" : "bg-amber-400"
              }`}
            />
          </span>
          <span className="text-[11.5px] text-zinc-300 font-medium">
            {allSystemsOk ? "All systems" : "Some degraded"}
          </span>
          {latency != null && (
            <span className="ml-auto text-[10px] text-zinc-500 shrink-0 font-mono">
              {latency}ms
            </span>
          )}
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {[
            { label: "IRCTC", key: "irctc" },
            { label: "NTES", key: "ntes" },
            { label: "Cal", key: "calendar" },
            { label: "Wlt", key: "wallet" },
          ].map((s) => (
            <div key={s.key} className="flex flex-col items-center gap-1 min-w-0">
              <span className={`w-full h-1 rounded ${toneFor(services[s.key])}`} />
              <span className="text-[9px] text-zinc-500 truncate font-mono">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* User pod — body content shared between the slot wrapper and the
          fallback button so both code paths render the same visual
          structure. */}
      {(() => {
        const podBody = (
          <>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white text-sm font-bold shrink-0">
              {initial}
            </div>
            <div className="flex-1 min-w-0 text-left">
              <div className="text-[12.5px] text-zinc-100 font-medium truncate">
                {user?.name || "Account"}
              </div>
              <div className="text-[10.5px] text-zinc-500 truncate">
                {user?.email || ""}
              </div>
            </div>
            <ChevronDown size={14} className="text-zinc-500" />
          </>
        );
        const className =
          "mx-3 mb-4 p-2.5 rounded-lg bg-zinc-900/60 border border-zinc-800/70 hover:border-zinc-700 flex items-center gap-2.5 transition-colors text-left w-[calc(100%-1.5rem)]";
        if (userMenuSlot) {
          return userMenuSlot({
            children: podBody,
            className,
            testId: "user-menu-btn",
          });
        }
        return (
          <button
            type="button"
            onClick={onUserMenu}
            className={className}
            data-testid="user-menu-btn"
          >
            {podBody}
          </button>
        );
      })()}
    </aside>
  );
};

export default Sidebar;
